import { createFeatureSelector, createSelector } from '@ngrx/store';
import { RideUtil } from '@uzurv/clara';
import { CancellationReasonConstants, isNullOrUndefined, ReservationStatusNameConstants, VehicleTypeConstants } from '@uzurv/core';
import { ReservationListItemVm } from '../../components/reservation/reservation-list-item-vm';
import { TripDetailState } from './trip-detail.reducer';

export function isEligibleForFareAdjustment(state: TripDetailState): boolean {
  if (state && state.trip && state.trip.isReservation) {
    if (state.trip.driver && state.trip.driver.emailAddress === 'lyft_dispatch_driver') {
      return (
        state.trip.status === ReservationListItemVm.RESERVATION_LIST_ITEM_STATUS_COMPLETED ||
        state.trip.status === ReservationListItemVm.RESERVATION_LIST_ITEM_STATUS_CANCELLED
      );
    } else {
      return state.trip.status === ReservationListItemVm.RESERVATION_LIST_ITEM_STATUS_COMPLETED || state.trip.cancellationReason?.name === CancellationReasonConstants.RIDE_INCIDENT;
    }
  } else {
    return false;
  }
}

export const getTripDetailState = createFeatureSelector<TripDetailState>('tripDetail');

export const getLoading = createSelector(getTripDetailState, (state: TripDetailState) => state.loading);

export const getEditActionsDisabled = createSelector(getTripDetailState, (state: TripDetailState) => state.editActionsDisabled);

export const getTrip = createSelector(getTripDetailState, (state: TripDetailState) => state.trip);

// TODO: Refactor this to use service levels once those are available in this model
export const getIsAmbulatory = createSelector(getTripDetailState, (state: TripDetailState) =>
  state.trip ? state.trip.rideRequestDetail.vehicleType.code === VehicleTypeConstants.VEHICLE_TYPE_CODE_BASIC : false
);

export const getCanEditTrip = createSelector(getTripDetailState, state =>
  state.trip
    ? state.trip.canEdit &&
      ((state.trip.rideRequestDetail.vehicleType.code === VehicleTypeConstants.VEHICLE_TYPE_CODE_BASIC && state.canEditBasicTrip) ||
        (state.trip.rideRequestDetail.vehicleType.code === VehicleTypeConstants.VEHICLE_TYPE_CODE_WAV && state.canEditWavTrip))
    : false
);

export const getCanEditPassengerCount = createSelector(getTripDetailState, (state: TripDetailState) => {
  if (isNullOrUndefined(state.trip)) {
    return false;
  }

  if (state.trip.cancelled) {
    return false;
  }

  if (state.trip.rideRequestDetail.vehicleType.code === VehicleTypeConstants.VEHICLE_TYPE_CODE_BASIC && !state.canEditBasicTrip) {
    return false;
  }

  if (state.trip.rideRequestDetail.vehicleType.code === VehicleTypeConstants.VEHICLE_TYPE_CODE_WAV && !state.canEditWavTrip) {
    return false;
  }

  if (!state.trip.isReservation) {
    return true;
  }

  return RideUtil.compareReservationStatus(state.trip.reservationStatus.name, ReservationStatusNameConstants.PICK_UP_ARRIVED) < 1;
});

export const getDriverProfilePicture = createSelector(getTripDetailState, (state: TripDetailState) => state.driverProfilePictureUrl);

export const getVehicle = createSelector(getTripDetailState, (state: TripDetailState) => (state.trip ? state.trip.vehicle : null));

export const getEligibleVehicles = createSelector(getTripDetailState, (state: TripDetailState) => state.eligibleVehicles);

export const getRoute = createSelector(getTripDetailState, (state: TripDetailState) => state.route);

export const getCompletedRoute = createSelector(getTripDetailState, (state: TripDetailState) => state.completedRoute);

export const getEnRoute = createSelector(getTripDetailState, (state: TripDetailState) => state.enRoute);

export const getMapCenter = createSelector(getTripDetailState, (state: TripDetailState) => state.mapCenter);

export const getRider = createSelector(getTripDetailState, (state: TripDetailState) =>
  state.trip ? state.trip.rideRequestDetail.organizationRider : null
);

export const getDriver = createSelector(getTripDetailState, (state: TripDetailState) => (state.trip ? state.trip.driver : null));

export const getDriverVehicles = createSelector(getTripDetailState, (state: TripDetailState) =>
  state.trip ? state.trip.driver?.vehicles : []
);

export const getDriverIsFavorite = createSelector(getTripDetailState, (state: TripDetailState) =>
  state.trip && state.trip.driver
    ? !isNullOrUndefined(
      state.trip.rideRequestDetail.organizationRider.rider.favoriteDrivers.find(driver => driver.id === state.trip.driver.id)
    )
    : false
);

export const getDriverIsIgnored = createSelector(getTripDetailState, (state: TripDetailState) =>
  state.trip && state.trip.driver
    ? !isNullOrUndefined(
      state.trip.rideRequestDetail.organizationRider.rider.ignoredDrivers.find(driver => driver.id === state.trip.driver.id)
    )
    : false
);

export const getChatMessages = createSelector(getTripDetailState, (state: TripDetailState) => state.chatMessages);

export const getSendingChatMessage = createSelector(getTripDetailState, (state: TripDetailState) => state.sendingChatMessage);

export const getNotes = createSelector(getTripDetailState, (state: TripDetailState) => state.notes);

export const getEditMode = createSelector(getTripDetailState, (state: TripDetailState) => state.editMode);

export const getFareReviewStatus = createSelector(getTripDetailState, (state: TripDetailState) => state.fareReviewStatus);

export const getFareReviewDetail = createSelector(getTripDetailState, (state: TripDetailState) => state.fareReviewDetail);

export const getLyftDispatchRide = createSelector(getTripDetailState, (state: TripDetailState) => state.lyftDispatchRide);

export const getLyftDispatchRideFareAdjustmentReasons = createSelector(
  getTripDetailState,
  (state: TripDetailState) => state.lyftDispatchRideFareAdjustmentReasons
);

export const getLyftDispatchRideReceipt = createSelector(getTripDetailState, (state: TripDetailState) => state.lyftDispatchRideReceipt);

export const getFareCalculationNotes = createSelector(getTripDetailState, (state: TripDetailState) => state.fareCalculationNotes);

export const getEstimateDriverFareCalculation = createSelector(
  getTripDetailState,
  (state: TripDetailState) => state.estimateDriverFareCalculation
);

export const getFareReviewNotes = createSelector(getTripDetailState, (state: TripDetailState) => state.fareReviewNotes);

export const getIsEligibleForFareAdjustment = createSelector(getTripDetailState, (state: TripDetailState) =>
  isEligibleForFareAdjustment(state)
);

export const getItinerary = createSelector(getTripDetailState, (state: TripDetailState) => state.itinerary);

export const getOrganizationServiceLevels = createSelector(getTripDetailState, (state: TripDetailState) => state.organizationServiceLevels);

export const getOrganizationRiderServiceLevels = createSelector(
  getTripDetailState,
  (state: TripDetailState) => state.organizationRiderServiceLevels
);

export const getEta = createSelector(getTripDetailState, state => state.eta);

export const getPaymentMethods = createSelector(getTripDetailState, state => state.paymentMethods);

export const getRiderFares = createSelector(getTripDetailState, state => state.riderFares);

export const isSubmittingRiderFares = createSelector(getTripDetailState, state => state.isSubmittingRiderFares);

export const isRiderFareCollected = createSelector(
  getTripDetailState,
  state => !isNullOrUndefined(state.riderFares) && state.riderFares.length > 0
);
